import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Skeleton,
  Chip,
} from "@mui/material";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { getGreeting } from "../../../styles/styles";
import { motion, AnimatePresence } from "framer-motion";
import { DashboardSideMenu } from "../../../static/staticdata";
import { useSelector } from "react-redux";
import { useAccess } from "../../Auth/AccessProvider";
import ReviewsRoundedIcon from "@mui/icons-material/ReviewsRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";

const SideNaveBarsecond = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openSide, setOpenSide] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [filteredMenu, setFilteredMenu] = useState([]);
  const [expandedCollapsedMenu, setExpandedCollapsedMenu] = useState(null);

  // Access control hooks
  const { userAccess } = useSelector((state) => state.user);
  const { getAllowedModules, isHighAccess } = useAccess();
  const allowedModules = getAllowedModules();

  // Filter menu based on user access
  useEffect(() => {
    setIsLoading(true);
    if (!userAccess || !Array.isArray(userAccess)) {
      setFilteredMenu(DashboardSideMenu);
      setIsLoading(false);
      return;
    }

    if (isHighAccess()) {
      setFilteredMenu(DashboardSideMenu);
      setIsLoading(false);
      return;
    }

    const filtered = DashboardSideMenu.reduce((acc, item) => {
      const isAllowedTop =
        item.accessId && allowedModules.includes(item.accessId);

      let allowedSubMenu = [];
      if (item.subMenu && Array.isArray(item.subMenu)) {
        allowedSubMenu = item.subMenu.filter(
          (subItem) =>
            subItem.accessId && allowedModules.includes(subItem.accessId)
        );
      }

      if (isAllowedTop || allowedSubMenu.length > 0) {
        acc.push({
          ...item,
          subMenu: allowedSubMenu.length > 0 ? allowedSubMenu : undefined,
        });
      }

      return acc;
    }, []);

    setFilteredMenu(filtered);
    setIsLoading(false);
  }, [userAccess]);

  // Handle accordion expansion
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }));
  };

  // Check if menu item is active
  const isMenuItemActive = (item) => {
    return (
      location.pathname === item.path ||
      (item.path === "/dashboard" &&
        location.pathname.endsWith("/dashboard")) ||
      (item.subMenu &&
        item.subMenu.some((subItem) =>
          location.pathname.endsWith(subItem.path)
        ))
    );
  };

  // Render submenu items with connecting lines
  const renderSubMenuItems = (subMenu, parentActive) => {
    return subMenu.map((subItem, subIndex) => {
      const isSubActive = location.pathname === subItem.path;

      const subMenuContent = (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            px: openSide ? "0.25rem" : "0.75rem",
            py: "0.375rem",
            ml: openSide ? 0 : "1rem",
            borderRadius: "0.375rem",
            color: isSubActive ? "#00B3B0" : "text.secondary",
            bgcolor: isSubActive ? "#00b3b01a" : "transparent",
            position: "relative",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            minHeight: "36px",
            "&:hover": {
              bgcolor: "#00b3b00d",
              transform: openSide ? "scale(1.05)" : "translateX(4px)",
              color: "#00B3B0",
            },
            // Connecting line to parent
            "&::before": !openSide
              ? {
                  content: '""',
                  position: "absolute",
                  left: "-0.75rem",
                  top: "50%",
                  width: "0.5rem",
                  height: "1px",
                  bgcolor: isSubActive ? "#00B3B0" : "divider",
                  transition: "all 0.2s ease",
                }
              : {},
            // Dot indicator for submenu
            "&::after": !openSide
              ? {
                  content: '""',
                  position: "absolute",
                  left: "-0.375rem",
                  top: "50%",
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  bgcolor: isSubActive ? "#00B3B0" : "divider",
                  transform: "translateY(-50%)",
                  transition: "all 0.2s ease",
                }
              : {},
          }}
        >
          <Box
            sx={{
              fontSize: "0.9rem",
              display: "flex",
              alignItems: "center",
              minWidth: "18px",
              justifyContent: "center",
            }}
          >
            {subItem.mIcon}
          </Box>
          {!openSide && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: isSubActive ? 600 : 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "0.8rem",
              }}
            >
              {subItem.mText}
            </Typography>
          )}
        </Box>
      );

      return (
        <motion.div
          key={subIndex}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{
            duration: 0.3,
            delay: subIndex * 0.05,
            ease: [0.4, 0, 0.2, 1],
          }}
        >
          <Tooltip
            title={subItem.mText}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "white",
                  color: "text.primary",
                  boxShadow: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                },
              },
              arrow: {
                sx: {
                  color: "white",
                  "&::before": {
                    border: "1px solid",
                    borderColor: "divider",
                  },
                },
              },
            }}
            disableHoverListener={!openSide}
          >
            <NavLink
              to={subItem.path}
              style={{ textDecoration: "none", display: "block" }}
            >
              {subMenuContent}
            </NavLink>
          </Tooltip>
        </motion.div>
      );
    });
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <>
      {[...Array(6)].map((_, i) => (
        <Box key={i} sx={{ px: 0.5, py: 0.25 }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={openSide ? "40px" : "36px"}
            sx={{ borderRadius: "0.375rem" }}
          />
        </Box>
      ))}
    </>
  );

  // Animation variants for collapsed submenu items
  const submenuVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      x: -20,
      transition: { duration: 0.2 },
    },
    visible: (index) => ({
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        duration: 0.3,
        delay: index * 0.08,
        ease: [0.4, 0, 0.2, 1],
      },
    }),
    exit: {
      opacity: 0,
      scale: 0.8,
      x: -20,
      transition: { duration: 0.2 },
    },
  };

  // Render menu items
  const renderMenuItems = () => {
    return filteredMenu?.map((item, index) => {
      const hasSubMenu = item?.subMenu && item?.subMenu?.length > 0;
      const isActive = isMenuItemActive(item);

      if (hasSubMenu) {
        if (openSide) {
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Box sx={{ mb: "0.5rem" }}>
                <Tooltip
                  title={item.mText}
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "white",
                        color: "text.primary",
                        boxShadow: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      },
                    },
                    arrow: {
                      sx: {
                        color: "white",
                        "&::before": {
                          border: "1px solid",
                          borderColor: "divider",
                        },
                      },
                    },
                  }}
                >
                  <Box
                    onClick={() =>
                      setExpandedCollapsedMenu(
                        expandedCollapsedMenu === index ? null : index
                      )
                    }
                    sx={{
                      p: "0.5rem",
                      borderRadius: "0.375rem",
                      border: "1px solid",
                      borderColor:
                        isActive || expandedCollapsedMenu === index
                          ? "#00B3B0"
                          : "divider",
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      mb: "0.375rem",
                      color:
                        isActive || expandedCollapsedMenu === index
                          ? "#00B3B0"
                          : "text.secondary",
                      bgcolor:
                        isActive || expandedCollapsedMenu === index
                          ? "#00b3b01a"
                          : "transparent",
                      position: "relative",
                      overflow: "visible",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0, 179, 176, 0.15)",
                        transform: "scale(1.05)",
                        bgcolor: "#00b3b00d",
                        borderColor: "#00B3B0",
                      },
                      "&::after":
                        expandedCollapsedMenu !== index
                          ? {
                              content: '""',
                              position: "absolute",
                              bottom: "-2px",
                              right: "4px",
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              bgcolor: isActive ? "#00B3B0" : "text.disabled",
                              opacity: 0.6,
                            }
                          : {},
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "1rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {item?.mIcon}
                    </Box>
                  </Box>
                </Tooltip>

                <AnimatePresence>
                  {expandedCollapsedMenu === index && (
                    <Box sx={{ ml: "0.75rem", position: "relative" }}>
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{
                          position: "absolute",
                          left: "-12px",
                          top: "0",
                          width: "1px",
                          backgroundColor: "#e0e0e0",
                          zIndex: 1,
                        }}
                      />

                      {item.subMenu.map((subItem, subIndex) => {
                        const isSubActive = location.pathname === subItem.path;
                        return (
                          <motion.div
                            key={`${index}-${subIndex}`}
                            custom={subIndex}
                            variants={submenuVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                          >
                            <Tooltip
                              title={subItem.mText}
                              placement="right"
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: "white",
                                    color: "text.primary",
                                    boxShadow: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                  },
                                },
                                arrow: {
                                  sx: {
                                    color: "white",
                                    "&::before": {
                                      border: "1px solid",
                                      borderColor: "divider",
                                    },
                                  },
                                },
                              }}
                            >
                              <NavLink
                                to={subItem.path}
                                style={{
                                  textDecoration: "none",
                                  display: "block",
                                }}
                              >
                                <Box
                                  sx={{
                                    p: "0.375rem",
                                    borderRadius: "0.25rem",
                                    border: "1px solid",
                                    borderColor: isSubActive
                                      ? "#00B3B0"
                                      : "divider",
                                    cursor: "pointer",
                                    transition:
                                      "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "100%",
                                    mb: "0.25rem",
                                    color: isSubActive
                                      ? "#00B3B0"
                                      : "text.secondary",
                                    bgcolor: isSubActive
                                      ? "#00b3b01a"
                                      : "transparent",
                                    opacity: 0.85,
                                    position: "relative",
                                    "&:hover": {
                                      boxShadow:
                                        "0 2px 8px rgba(0, 179, 176, 0.12)",
                                      transform: "scale(1.05)",
                                      bgcolor: "#00b3b00d",
                                      opacity: 1,
                                      borderColor: "#00B3B0",
                                    },
                                    // Connection line to parent
                                    "&::before": {
                                      content: '""',
                                      position: "absolute",
                                      left: "-12px",
                                      top: "50%",
                                      width: "8px",
                                      height: "1px",
                                      bgcolor: isSubActive
                                        ? "#00B3B0"
                                        : "divider",
                                      transform: "translateY(-50%)",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      fontSize: "0.85rem",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {subItem?.mIcon}
                                  </Box>
                                </Box>
                              </NavLink>
                            </Tooltip>
                          </motion.div>
                        );
                      })}
                    </Box>
                  )}
                </AnimatePresence>
              </Box>
            </motion.div>
          );
        }

        // Render accordion for items with submenu in expanded mode
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: index * 0.05 }}
          >
            <Box sx={{ mb: "0.5rem" }}>
              <Accordion
                expanded={expandedAccordions?.[item.mText] || false}
                onChange={handleAccordionChange(item.mText)}
                sx={{
                  p: "0rem",
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: isActive ? "#00B3B0" : "divider",
                  borderRadius: "0.375rem !important",
                  bgcolor: isActive ? "#00b3b005" : "transparent",
                  overflow: "hidden",
                  "&:before": {
                    display: "none",
                  },
                  "& .MuiAccordionSummary-root": {
                    minHeight: "auto",
                    px: "0.75rem",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "#00b3b00d",
                    },
                  },
                  "& .MuiAccordionDetails-root": {
                    px: 0,
                    py: "0.5rem",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fafafa",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreRoundedIcon
                      sx={{
                        color: isActive ? "#00B3B0" : "text.secondary",
                        fontSize: "1rem",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: "1rem",
                        display: "flex",
                        alignItems: "center",
                        color: isActive ? "#00B3B0" : "text.secondary",
                      }}
                    >
                      {item.mIcon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? "#00B3B0" : "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                        fontSize: "0.85rem",
                      }}
                    >
                      {item.mText}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Box
                    sx={{
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: "0.5rem",
                        top: 0,
                        bottom: 0,
                        width: "1px",
                        bgcolor: "divider",
                      },
                    }}
                  >
                    <AnimatePresence>
                      {expandedAccordions?.[item.mText] &&
                        renderSubMenuItems(item.subMenu, isActive)}
                    </AnimatePresence>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </motion.div>
        );
      }

      // Render regular menu items without submenu
      const menuItemContent = (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, delay: index * 0.05 }}
          whileHover={{ scale: 1.02 }}
        >
          <Box
            sx={{
              p: openSide ? "0.5rem" : "0.5rem 0.75rem",
              borderRadius: "0.375rem",
              border: "1px solid",
              borderColor: isActive ? "#00B3B0" : "divider",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              flexDirection: openSide ? "column" : "row",
              justifyContent: openSide ? "center" : "flex-start",
              alignItems: "center",
              gap: openSide ? "0.125rem" : "0.5rem",
              width: "100%",
              mb: "0.5rem",
              color: isActive ? "#00B3B0" : "text.secondary",
              bgcolor: isActive ? "#00b3b01a" : "transparent",
              minHeight: openSide ? "40px" : "36px",
              "&:hover": {
                boxShadow: "0 2px 8px rgba(0, 179, 176, 0.12)",
                transform: "translateY(-1px)",
                bgcolor: "#00b3b00d",
                borderColor: "#00B3B0",
              },
            }}
          >
            <Box
              sx={{
                fontSize: openSide ? "1rem" : "1rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              {item?.mIcon}
            </Box>
            {!openSide && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                }}
              >
                {item?.mText}
              </Typography>
            )}
          </Box>
        </motion.div>
      );

      return (
        <Tooltip
          key={index}
          title={item.mText}
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "white",
                color: "text.primary",
                boxShadow: 2,
                border: "1px solid",
                borderColor: "divider",
                fontSize: "0.75rem",
                fontWeight: 500,
              },
            },
            arrow: {
              sx: {
                color: "white",
                "&::before": {
                  border: "1px solid",
                  borderColor: "divider",
                },
              },
            },
          }}
          disableHoverListener={!openSide}
        >
          <NavLink
            to={item.path}
            style={{ textDecoration: "none", display: "block" }}
          >
            {menuItemContent}
          </NavLink>
        </Tooltip>
      );
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: openSide ? "4.5rem" : "16rem",
        bgcolor: "background.paper",
        borderRight: "1px solid",
        borderColor: "divider",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        position: "sticky",
        top: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: "0.75rem",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: openSide ? "center" : "space-between",
          alignItems: "center",
          minHeight: "60px",
        }}
      >
        {!openSide && (
          <Typography
            variant="h6"
            sx={{
              color: "#00B3B0",
              fontFamily: "cursive",
              fontWeight: 600,
              whiteSpace: "nowrap",
              transition: "all 0.2s",
            }}
          >
            {getGreeting()}
          </Typography>
        )}

        <IconButton
          color="primary"
          size="small"
          onClick={() => {
            setOpenSide(!openSide);
            setExpandedAccordions({}); // Fixed: Changed from null to {}
          }}
          sx={{
            color: "#00B3B0",
            bgcolor: "rgba(0, 179, 176, 0.08)",
            "&:hover": {
              bgcolor: "rgba(0, 179, 176, 0.16)",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease",
          }}
        >
          {openSide ? (
            <ChevronRightRoundedIcon sx={{ fontSize: "1.2rem" }} />
          ) : (
            <ChevronLeftRoundedIcon sx={{ fontSize: "1.2rem" }} />
          )}
        </IconButton>
      </Box>

      {/* Menu Items */}
      <Box
        sx={{
          // border: "1px solid red",
          overflowY: "auto",
          overflowX: "hidden",
          p: "0.75rem 0.5rem",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          scrollbarWidth: "thin",
          height: "75%",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#0000001a",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        }}
      >
        {isLoading ? renderSkeleton() : renderMenuItems()}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <Button
          variant="text"
          color="primary"
          size="small"
          startIcon={<ReviewsRoundedIcon sx={{ fontSize: "0.7rem" }} />}
        >
          Feed Back
        </Button>
        <Button
          variant="text"
          color="error"
          size="small"
          startIcon={<BugReportRoundedIcon sx={{ fontSize: "0.7rem" }} />}
        >
          Issue
        </Button>
      </Box>
    </Box>
  );
};

export default SideNaveBarsecond;
